import { createSelector, createSlice, createEntityAdapter } from "@reduxjs/toolkit";
import Provider from "provider";
import { buildModelReducer } from "../../redux/reducers";
import { createCloseListenerAction, createOpenListenerAction, modelsUpdated } from "../../redux/actions";

import { narrationEntity } from "../../models/narration";
import logging from "utils/logging";
import * as ttsService from "./ttsService";

const log = logging.getLogger("host-state-slice");
log.setLevel('warn');

/**
 * Host Status Enum Values
 * Used to track the current state of the host
 */
export const HOST_STATUS = {
  IDLE: 'IDLE',
  READY_TO_SPEAK: 'READY_TO_SPEAK',
  SPEAKING: 'SPEAKING',
  PAUSED: 'PAUSED',
  ERROR: 'ERROR'
};

/**
 * Narration Key Constants
 * Used to identify specific narrations
 */
export const NARRATION_KEYS = {
  LOBBY_WELCOME: 'LOBBY_WELCOME',
  INSTRUCTIONS: 'INSTRUCTIONS',
  ROUND_INTRO: 'ROUND_INTRO',
  ROUND_INTRO_CLOSING: 'ROUND_INTRO_CLOSING',
  INSTRUCTIONS_DONE: 'INSTRUCTIONS_DONE',
  JUDGING_RESULTS: 'JUDGING_RESULTS',
  ROUND_COMPLETE: 'ROUND_COMPLETE',
  DAILY_DOUBLE_REVEAL: 'DAILY_DOUBLE_REVEAL',
  DAILY_DOUBLE_WAGER: 'DAILY_DOUBLE_WAGER',
  FINAL_INTRO: 'FINAL_INTRO',
  FINAL_CATEGORY_BUILDUP: 'FINAL_CATEGORY_BUILDUP',
  FINAL_CATEGORY_REVEAL: 'FINAL_CATEGORY_REVEAL',
  FINAL_WAGER_INSTRUCTIONS: 'FINAL_WAGER_INSTRUCTIONS',
  FINAL_QUESTION_INTRO: 'FINAL_QUESTION_INTRO',
  FINAL_QUESTION_READ: 'FINAL_QUESTION_READ',
  FINAL_PLAYER_REVEAL: 'FINAL_PLAYER_REVEAL',
  FINAL_PLAYER_WAGER: 'FINAL_PLAYER_WAGER',
  FINAL_WINNER: 'FINAL_WINNER',
  // Final Jeopardy reveal sub-step narration keys
  FINAL_REVEAL_INTRO: 'FINAL_REVEAL_INTRO',
  FINAL_PLAYER_INTRO: 'FINAL_PLAYER_INTRO',
  FINAL_ANSWER_INTRO: 'FINAL_ANSWER_INTRO',
  FINAL_ANSWER_REACTION_WAGER_INTRO: 'FINAL_ANSWER_REACTION_WAGER_INTRO',
  FINAL_WAGER_REACTION: 'FINAL_WAGER_REACTION',
  FINAL_STANDINGS: 'FINAL_STANDINGS',
};

/**
 * Moment Type Constants
 * Used to identify which moment type a narration belongs to
 */
export const MOMENT_TYPES = {
  LOBBY_WELCOME: 'LOBBY_WELCOME',
  PLAYER_JOINED: 'PLAYER_JOINED',
  INSTRUCTIONS: 'INSTRUCTIONS',
  PLAYER_READY: 'PLAYER_READY',
  INSTRUCTIONS_DONE: 'INSTRUCTIONS_DONE',
  ROUND_INTRO: 'ROUND_INTRO',
  ROUND_INTRO_CLOSING: 'ROUND_INTRO_CLOSING',
  CATEGORY_AUDIO: 'CATEGORY_AUDIO',
  QUESTION_SELECTED: 'QUESTION_SELECTED',
  READ_QUESTION: 'READ_QUESTION',
  JUDGING_COMPLETE: 'JUDGING_COMPLETE',
  ROUND_COMPLETE: 'ROUND_COMPLETE',
  DAILY_DOUBLE_REVEAL: 'DAILY_DOUBLE_REVEAL',
  DAILY_DOUBLE_WAGER: 'DAILY_DOUBLE_WAGER',
  FINAL_INTRO: 'FINAL_INTRO',
  FINAL_CATEGORY_BUILDUP: 'FINAL_CATEGORY_BUILDUP',
  FINAL_CATEGORY_REVEAL: 'FINAL_CATEGORY_REVEAL',
  FINAL_WAGER_INSTRUCTIONS: 'FINAL_WAGER_INSTRUCTIONS',
  FINAL_QUESTION_INTRO: 'FINAL_QUESTION_INTRO',
  FINAL_QUESTION_READ: 'FINAL_QUESTION_READ',
  FINAL_PLAYER_REVEAL: 'FINAL_PLAYER_REVEAL',
  FINAL_PLAYER_WAGER: 'FINAL_PLAYER_WAGER',
  FINAL_WINNER: 'FINAL_WINNER',
  // Final Jeopardy reveal sub-step moment types
  FINAL_REVEAL_INTRO: 'FINAL_REVEAL_INTRO',
  FINAL_PLAYER_INTRO: 'FINAL_PLAYER_INTRO',
  FINAL_ANSWER_INTRO: 'FINAL_ANSWER_INTRO',
  FINAL_ANSWER_REACTION_WAGER_INTRO: 'FINAL_ANSWER_REACTION_WAGER_INTRO',
  FINAL_WAGER_REACTION: 'FINAL_WAGER_REACTION',
  FINAL_STANDINGS: 'FINAL_STANDINGS',
};

const narrationAdapter = createEntityAdapter();
const narrationInitialState = narrationAdapter.getInitialState();
const narrationStateSlice = createSlice({
  name: "narration",
  initialState: narrationInitialState,
  reducers: {},
  extraReducers: (builder) => {
    buildModelReducer({
      builder,
      entity: narrationEntity,
      loadingKey: "loadingNarrations",
      adapter: narrationAdapter,
    });
  }
});
export const narrationReducer = narrationStateSlice.reducer;

/**
 * Initial state for the host slice
 * Only includes client-side state that's not stored in Firebase
 */
// Module-level storage for pending retry (can't store functions/objects in Redux)
let pendingRetryParams = null;

const initialState = {
  audioPlaying: false,
  captionsVisible: false,
  hasError: false,
  errorMessage: null,
  // Reference to the current audio element for controlling playback
  audioRef: null,
  pendingNarration: null,
  loadingNarrations: false,
  // Track if audio was paused and when
  pausedAtTime: null,
  // Track if user interaction is needed for audio autoplay
  audioInteractionNeeded: false,
};

/**
 * Host State Slice
 * Manages client-side host state and provides thunks for host operations
 */

const hostStateSlice = createSlice({
  name: "host",
  initialState,
  reducers: {
    /**
     * Update the client-side audio playing state
     */
    setAudioPlaying(state, action) {
      state.audioPlaying = action.payload;
    },
    
    /**
     * Update the client-side captions visibility
     */
    setCaptionsVisible(state, action) {
      state.captionsVisible = action.payload;
    },
    
    /**
     * Set an error state with a message
     */
    setHostError(state, action) {
      state.hasError = true;
      state.errorMessage = action.payload;
    },
    
    /**
     * Clear any error state
     */
    clearHostError(state) {
      state.hasError = false;
      state.errorMessage = null;
    },
    
    /**
     * Store a reference to the current audio element
     */
    setAudioRef(state, action) {
      state.audioRef = action.payload;
    },

    setPendingNarration(state, action) {
      state.pendingNarration = action.payload;
    },

    loadingNarrations(state, action) {
      state.loadingNarrations = action.payload;
    },

    setPausedAtTime(state, action) {
      state.pausedAtTime = action.payload;
    },

    clearPausedAtTime(state) {
      state.pausedAtTime = null;
    },

    setAudioInteractionNeeded(state, action) {
      state.audioInteractionNeeded = action.payload;
    },
  }
});

// Extract action creators
export const {
  setAudioPlaying,
  setCaptionsVisible,
  setHostError,
  clearHostError,
  setAudioRef,
  setPendingNarration,
  setPausedAtTime,
  clearPausedAtTime,
  setAudioInteractionNeeded
} = hostStateSlice.actions;

/**
 * Request narration generation for a specific game state and context
 */
export const requestNarration = ({ gameId, narrationKey, momentType, state, context }) => async (dispatch) => {
  log.info(`Requesting narration for game ${gameId}, key ${narrationKey}`);
  dispatch(clearHostError());
  
  try {
    // First, request the narration text
    const result = await Provider.requestNarration(gameId, narrationKey, momentType, state, context);
    log.info("Narration result:", result);
    return result;
  } catch (error) {
  log.error("Error requesting narration:", error);
    dispatch(setHostError(`Failed to generate narration: ${error.message}`));
    throw error;
  }
};

export const requestConversationalNarration = ({ gameId, narrationKey, momentType, context, conversationId }) => async (dispatch) => {
  log.info(`Requesting conversational narration for game ${gameId}, key ${narrationKey}, conversation ${conversationId}`);
  dispatch(clearHostError());

  try {
    const result = await Provider.requestConversationalNarration(gameId, narrationKey, momentType, context, conversationId);
    log.info("Conversational narration result:", result);
    return result;
  } catch (error) {
    log.error("Error requesting conversational narration:", error);
    dispatch(setHostError(`Failed to generate narration: ${error.message}`));
    throw error;
  }
};

export const narrationReady = ({ gameId, narrationKey }) => (dispatch, getState) => {
  log.info(`Narration ready for game ${gameId}, key ${narrationKey}`);
  
  // Check if we already have this narration pending to avoid duplicate triggers
  const currentState = getState();
  const currentPending = currentState.host?.pendingNarration;
  
  if (currentPending === narrationKey) {
    log.debug(`Narration ${narrationKey} is already pending, skipping duplicate ready notification`);
    return;
  }
  
  dispatch(setPendingNarration(narrationKey));
}

/**
 * Request audio generation for a narration
 */
export const requestAudio = ({ gameId, narrationKey }) => async (dispatch) => {
  log.info(`Requesting audio for game ${gameId}, key ${narrationKey}`);
  dispatch(clearHostError());
  
  try {
    const result = await Provider.requestAudio(gameId, narrationKey);
    log.info("Audio result:", result);
    return result;
  } catch (error) {
    log.error("Error requesting audio:", error);
    dispatch(setHostError(`Failed to generate audio: ${error.message}`));
    throw error;
  }
};

/**
 * Play a narration with the given key
 * This will show captions and play the audio
 */
export const playNarration = ({ gameId, narration, onComplete }) => async (dispatch, getState) => {
  log.info(`Playing narration for game ${gameId}, key ${narration.key}`);
  
  try {
    // Get the game object from state
    const state = getState();
    const game = state.game?.game;
    if (!game) {
      throw new Error(`Game ${gameId} not found in state`);
    }
    
    // Get the narration from the game's host state
    if (!narration) {
      throw new Error(`Missing parameter narration: ${narration}`);
    }
    
    // Check if already playing or recently played
    if (narration.audioStatus === "PLAYING") {
      log.warn(`Narration ${narration.key} is already playing, not starting playback again`);
      return { success: false, error: "Already playing" };
    }

    const hasAudio = narration.audioStatus === "READY" && narration.audioURL;

    if (!hasAudio) {
      log.warn(`No audio available for narration ${narration.key} (audioStatus: ${narration.audioStatus}), advancing without audio`);
    }

    log.debug('Showing captions');
    // Show captions
    dispatch(clearHostError());
    dispatch(setCaptionsVisible(true));
    dispatch(setAudioPlaying(hasAudio));

    log.debug('Updating host state to SPEAKING');

    await Provider.updateHostState(gameId, {
      status: HOST_STATUS.SPEAKING,
      currentNarrationKey: narration.key,
    });

    await Provider.updateNarration(gameId, narration.id, {
      textStatus: "SHOWING",
      audioStatus: hasAudio ? "PLAYING" : narration.audioStatus,
      playStartedAt: new Date().toISOString()
    });

    try {
      if (hasAudio) {
        log.debug('Playing audio: ', narration.audioURL);
        // Use ttsService to play audio and get the audio element
        const { audio, promise } = await ttsService.playAudio(narration.audioURL);

        // Store the audio reference in state for pause/resume functionality
        dispatch(setAudioRef(audio));

        // Wait for audio playback to complete (or be interrupted)
        await promise;

        // Clear the audio reference
        dispatch(setAudioRef(null));

        log.info(`Audio playback completed for narration ${narration.key}`);
      } else {
        // No audio — show captions for a duration based on text length, then continue
        const words = (narration.text || '').split(/\s+/).length;
        const displayMs = Math.max(3000, Math.min(words * 300, 10000));
        log.info(`No audio: showing captions for ${displayMs}ms (${words} words)`);
        await new Promise(resolve => setTimeout(resolve, displayMs));
      }

      dispatch(setAudioPlaying(false));
      dispatch(setCaptionsVisible(false));

      // Update host state to mark completion in a single transaction
      await Provider.updateHostState(gameId, {
        status: HOST_STATUS.IDLE,
        currentNarrationKey: null, // Clear current narration key
      });

      await Provider.updateNarration(gameId, narration.id, {
        textStatus: "SHOWN",
        audioStatus: hasAudio ? "PLAYED" : narration.audioStatus,
        playCompletedAt: new Date().toISOString()
      });

      // Execute onComplete callback if provided
      if (typeof onComplete === 'function') {
        log.debug(`Executing onComplete callback for narration ${narration.key}`);
        onComplete(gameId, dispatch, getState);
      }
    } catch (error) {
      // Check if this is a browser autoplay policy error
      if (error.name === 'NotAllowedError') {
        log.warn(`Audio autoplay blocked for narration ${narration.key} - user interaction required`);
        dispatch(setAudioPlaying(false));
        dispatch(setCaptionsVisible(false));
        dispatch(setAudioRef(null));

        // Reset host state back to IDLE (not ERROR - this is recoverable)
        await Provider.updateHostState(gameId, {
          status: HOST_STATUS.IDLE,
          currentNarrationKey: null,
        });

        await Provider.updateNarration(gameId, narration.id, {
          textStatus: "READY",
          audioStatus: "READY",
        });

        // Store retry params and signal the UI to show interaction overlay
        pendingRetryParams = { gameId, narration, onComplete };
        dispatch(setAudioInteractionNeeded(true));
        return { success: false, error: 'autoplay_blocked' };
      }

      log.error(`Error playing audio for narration ${narration.key}:`, error);
      dispatch(setAudioPlaying(false));
      dispatch(setCaptionsVisible(false));
      dispatch(setPendingNarration(null));
      dispatch(setAudioRef(null));

      await Provider.updateHostState(gameId, {
        status: HOST_STATUS.ERROR,
        currentNarrationKey: null,
      });

      await Provider.updateNarration(gameId, narration.id, {
        textStatus: "ERROR",
        audioStatus: "ERROR",
        error: error.message
      });

      throw error;
    }
    
    return { success: true };
  } catch (error) {
    log.error(`Failed to play narration ${narration.key}:`, error);
    dispatch(setHostError(`Failed to play narration: ${error.message}`));
    return { success: false, error };
  }
};

/**
 * Retry narration playback after user interaction unlocks audio
 */
export const retryPendingNarration = () => async (dispatch) => {
  dispatch(setAudioInteractionNeeded(false));

  if (!pendingRetryParams) {
    log.warn('No pending narration to retry');
    return;
  }

  const { gameId, narration, onComplete } = pendingRetryParams;
  pendingRetryParams = null;

  log.info(`Retrying narration playback for ${narration.key} after user interaction`);
  dispatch(playNarration({ gameId, narration, onComplete }));
};

/**
 * Stop the current narration playback
 */
export const stopNarration = () => (dispatch, getState) => {
  const state = getState();
  const audioRef = selectAudioRef(state);
  
  if (audioRef) {
    audioRef.pause();
    audioRef.currentTime = 0;
  }
  
  dispatch(setAudioPlaying(false));
  dispatch(setCaptionsVisible(false));
  dispatch(setAudioRef(null)); // Clear audio reference
};

/**
 * Pause the current narration playback
 */
export const pauseNarration = ({ gameId }) => async (dispatch, getState) => {
  const state = getState();
  const audioRef = selectAudioRef(state);
  
  if (audioRef) {
    audioRef.pause();
    // Store the current time when paused
    dispatch(setPausedAtTime(audioRef.currentTime));
  }
  
  dispatch(setAudioPlaying(false));
  
  // Update host state to PAUSED
  await Provider.updateHostState(gameId, {
    status: HOST_STATUS.PAUSED,
  });
  
  log.info('Narration paused');
};

/**
 * Resume the current narration playback
 */
export const resumeNarration = ({ gameId }) => async (dispatch, getState) => {
  const state = getState();
  const audioRef = selectAudioRef(state);
  const pausedAtTime = state.host.pausedAtTime;
  
  if (audioRef) {
    try {
      if (pausedAtTime != null) {
        audioRef.currentTime = pausedAtTime;
      }
      await audioRef.play();
      dispatch(setAudioPlaying(true));
      dispatch(clearPausedAtTime());
      
      // Update host state back to SPEAKING
      await Provider.updateHostState(gameId, {
        status: HOST_STATUS.SPEAKING,
      });
      
      log.info('Narration resumed');
    } catch (error) {
      log.error('Error resuming narration:', error);
      dispatch(setHostError(`Failed to resume narration: ${error.message}`));
    }
  } else {
    log.warn('No audio reference available for resume');
  }
};

/**
 * Interrupt the current narration playback
 */
export const interruptNarration = ({ gameId, narrationId }) => async (dispatch, getState) => {
  const state = getState();
  const audioRef = selectAudioRef(state);
  
  if (audioRef) {
    audioRef.pause();
    audioRef.currentTime = 0;
    // Trigger the interrupt event to resolve the promise
    ttsService.interruptAudio(audioRef);
  }
  
  dispatch(setAudioPlaying(false));
  dispatch(setCaptionsVisible(false));
  dispatch(setAudioRef(null));
  dispatch(clearPausedAtTime());
  
  // Update narration status to INTERRUPTED
  if (narrationId) {
    await Provider.updateNarration(gameId, narrationId, {
      textStatus: "INTERRUPTED",
      audioStatus: "INTERRUPTED",
      interruptedAt: new Date().toISOString()
    });

  // Update host state to IDLE
  await Provider.updateHostState(gameId, {
    status: HOST_STATUS.IDLE,
    currentNarrationKey: null,
  });
  }
  
  log.info('Narration interrupted');
};

/**
 * Sets the current narration key
 * This determines which narration is displayed in closed captions
 */
export const setCurrentNarrationKey = ({ gameId, narrationKey }) => async (dispatch) => {
  log.info(`Setting current narration key for game ${gameId} to ${narrationKey}`);
  
  try {
    await Provider.updateHostState(gameId, {
      currentNarrationKey: narrationKey
    });
    
    return { success: true };
  } catch (error) {
    log.error(`Failed to set current narration key: ${error.message}`);
    dispatch(setHostError(`Failed to set current narration key: ${error.message}`));
    return { success: false, error };
  }
};

const watchNarrationsActionBase = createOpenListenerAction(
  "host/watch_narrations",
  "watch-narrations"
);

const stopWatchingNarrationsActionBase = createCloseListenerAction(
  "host/stop_watching_narrations",
  "watch-narrations"
);

const watchNarrationsAction = (gameId) => (dispatch) => {
  dispatch(hostStateSlice.actions.loadingNarrations(true));
  dispatch(watchNarrationsActionBase({ gameId }, async () => {
    log.info("Watching narrations for game: ", gameId);
    return await Provider.watchNarrations(gameId, (narrationsUpdate) => {
      log.debug("Got updated narrations: ", narrationsUpdate);
      dispatch(modelsUpdated(narrationsUpdate, narrationEntity));
    });
  }));
};


const stopWatchingNarrationsAction = (gameId) => (dispatch) => {
  dispatch(stopWatchingNarrationsActionBase({ gameId }));
};

export { watchNarrationsAction, stopWatchingNarrationsAction };

/* -- Selectors -- */

export const { 
  selectById: selectNarrationById,
  selectAll: selectAllNarrations,
} = narrationAdapter.getSelectors((state) => state.narration);

// Base selectors
const selectHostSlice = (state) => state.host;
const selectGameObject = (state) => state.game?.game;
const selectNarrationsSlice = (state) => state.narration;

// Client-side state selectors
export const selectClientState = createSelector(
  selectHostSlice, 
  (hostSlice) => hostSlice
);

export const selectAudioPlaying = createSelector(
  selectClientState,
  (clientState) => clientState.audioPlaying
);

export const selectCaptionsVisible = createSelector(
  selectClientState,
  (clientState) => clientState.captionsVisible
);

export const selectHostHasError = createSelector(
  selectClientState,
  (clientState) => clientState.hasError
);

export const selectHostErrorMessage = createSelector(
  selectClientState,
  (clientState) => clientState.errorMessage
);

export const selectAudioRef = createSelector(
  selectClientState,
  (clientState) => clientState.audioRef
);

// Server-side state selectors (from game object)
export const selectHostState = createSelector(
  selectGameObject,
  (game) => game?.hostState || null
);

export const selectHostStatus = createSelector(
  selectHostState,
  (hostState) => hostState?.status || HOST_STATUS.IDLE
);

export const selectCurrentNarrationKey = createSelector(
  selectHostState,
  (hostState) => hostState?.currentNarrationKey || null
);

export const selectNarrations = createSelector(
  selectNarrationsSlice,
  (narrationsSlice) => Object.values(narrationsSlice.entities)
);

export const selectCurrentNarration = createSelector(
  [selectNarrations, selectCurrentNarrationKey],
  (narrations, currentKey) => {
    if (!currentKey || !narrations) return null;
    return narrations.find(narration => narration.key === currentKey);
  }
);

export const selectAudioInteractionNeeded = createSelector(
  selectClientState,
  (clientState) => clientState.audioInteractionNeeded
);

export const selectNarrationsLoading = createSelector(
  selectHostState,
  (hostState) => hostState?.loadingNarrations || false
);

// Get a specific narration by key
export const makeSelectNarrationByKey = () => 
  createSelector(
    [selectNarrations, (state, narrationKey) => narrationKey],
    (narrations, narrationKey) => {
      if (!narrationKey || !narrations) return null;
      return narrations.find(narration => narration.key === narrationKey);
    }
  );

export default hostStateSlice.reducer; 